import Image from "next/image";
import Link from "next/link";
import { langPath, type Dict, type Lang } from "@/lib/i18n";
import type { OrganizationRef } from "@/lib/lecturehub/types";

/**
 * Reverse-link chip row (roadmap O31): the organizations a module or
 * instructor belongs to, in the admin-defined order, each linking to its
 * /organizations/[id]/[slug] profile (phase 2).
 *
 * No "use client" on purpose: it renders from both the client detail views
 * (CourseView & co.) and the server-rendered author page, so the resolved
 * Dict is threaded in as a prop instead of read via useDict().
 */
export function OrganizationChips({
  organizations,
  t,
  lang,
  className,
}: {
  organizations?: OrganizationRef[] | null;
  t: Dict;
  lang: Lang;
  className?: string;
}) {
  if (!organizations || organizations.length === 0) return null;
  return (
    <section className={className}>
      <h2 className="text-[12px] uppercase tracking-[0.14em] font-bold text-foreground/55">
        {organizations.length === 1 ? t.detail.organizationOne : t.detail.organizationMany}
      </h2>
      <ul className="mt-3 flex flex-wrap gap-2">
        {organizations.map((org) => {
          const label = org.displayName ?? org.name;
          return (
            <li key={org.id}>
              <Link
                href={langPath(lang, `/organizations/${org.id}/${org.stub}`)}
                className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3.5 py-1.5 transition-colors hover:border-border hover:bg-muted/60"
              >
                {org.image?.uri ? (
                  <span className="relative size-5 shrink-0 overflow-hidden rounded-full bg-muted">
                    {/* alt="" — the organisation's name is the chip's text beside this 20px logo. */}
                    <Image src={org.image.uri} alt="" fill sizes="20px" className="object-cover" />
                  </span>
                ) : null}
                <span className="text-[13px] font-display font-semibold leading-none text-foreground">
                  {label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
